/**
 * /password-reset/confirm - token-consume page for the customer-portal
 * password reset flow. The email sent by
 * `POST /v1/portal/auth/password-reset/request` links here with
 * `?token=…`. The customer enters a new password + POSTs to
 * `/v1/portal/auth/password-reset/confirm`.
 *
 * Was missing - emails linked at this URL but the SPA had no route
 * -> react-router fell through to <Navigate to="/" /> -> ProtectedRoute
 * bounced to /login, leaving the customer unable to actually set the
 * new password. Symptom Daniel reported in chat.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { OrbotoLockup } from '../components/OrbotoLockup';
import { LegalFooter } from '../components/LegalFooter';

export function PasswordResetConfirmPage() {
  const { t } = useTranslation('password-reset');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // No-token short-circuit: redirect to the request page so the
  // customer doesn't see a confusing form.
  useEffect(() => {
    if (!token) navigate('/password-reset', { replace: true });
  }, [token, navigate]);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (password.length < 12) {
      setError(t('confirm.error.tooShort'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('confirm.error.mismatch'));
      return;
    }
    setSubmitting(true);
    try {
      await api('/v1/portal/auth/password-reset/confirm', {
        method: 'POST',
        body: { token, password },
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(t('confirm.error.expired'));
      } else if (err instanceof ApiError && err.status === 400) {
        const code =
          err.body && typeof err.body === 'object' && 'error' in err.body
            ? String((err.body as { error: unknown }).error)
            : null;
        setError(code === 'weak_password' ? t('confirm.error.weak') : t('confirm.error.generic'));
      } else {
        setError(t('confirm.error.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-gray-50 dark:bg-gray-800 px-4 text-gray-900 dark:text-gray-100">
      <div className="w-full max-w-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center gap-2">
          <OrbotoLockup className="h-7 w-auto" />
          <div className="text-center text-xs text-gray-500">{t('confirm.header')}</div>
        </div>

        {done ? (
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <p>{t('confirm.done')}</p>
            <Link
              to="/login"
              className="mt-6 inline-block rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700"
            >
              {t('confirm.goToSignIn')}
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('confirm.intro')}
            </p>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-700 dark:text-gray-300">{t('confirm.newPassword')}</span>
              <input
                type="password"
                name="new-password"
                autoComplete="new-password"
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-700 dark:text-gray-300">{t('confirm.confirmPassword')}</span>
              <input
                type="password"
                name="confirm-password"
                autoComplete="new-password"
                required
                minLength={12}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="rounded border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              />
            </label>

            {error && (
              <div
                role="alert"
                className="rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {submitting ? t('confirm.submitting') : t('confirm.submit')}
            </button>

            <Link
              to="/login"
              className="text-center text-sm text-gray-500 hover:text-brand-500"
            >
              {t('confirm.backToSignIn')}
            </Link>
          </form>
        )}
        <LegalFooter className="mt-8" />
      </div>
    </div>
  );
}
